<template>
  <TableLayout :permissions="['system:ai-config:query']">
    <!-- 搜索表单 -->
    <el-form
      ref="searchForm"
      slot="search-form"
      :model="searchForm"
      label-width="80px"
      inline
    >
      <el-form-item label="模型类型" prop="modelType">
        <el-select v-model="searchForm.modelType" placeholder="全部" clearable>
          <el-option label="DeepSeek" value="deepseek"></el-option>
          <el-option label="Gemini" value="gemini"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item label="是否启用" prop="isActive">
        <el-select v-model="searchForm.isActive" placeholder="全部" clearable>
          <el-option label="启用" :value="true"></el-option>
          <el-option label="禁用" :value="false"></el-option>
        </el-select>
      </el-form-item>
      <section>
        <el-button type="primary" icon="el-icon-search" @click="search"
          >搜索</el-button
        >
        <el-button @click="reset">重置</el-button>
      </section>
    </el-form>

    <!-- 表格区域 -->
    <template v-slot:table-wrap>
      <ul class="toolbar">
        <li>
          <el-button
            type="primary"
            icon="el-icon-plus"
            @click="$refs.operaWindow.open('新建配置')"
            v-permissions="['system:ai-config:create']"
            >新建配置</el-button
          >
        </li>
        <li>
          <el-button
            icon="el-icon-delete"
            @click="deleteByIdInBatch"
            v-permissions="['system:ai-config:delete']"
            >删除</el-button
          >
        </li>
      </ul>

      <el-table
        v-loading="isWorking.search"
        :data="tableData.list"
        stripe
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="modelType" label="模型类型" min-width="100">
          <template slot-scope="{ row }">
            <el-tag v-if="row.modelType === 'deepseek'" type="primary"
              >DeepSeek</el-tag
            >
            <el-tag v-else-if="row.modelType === 'gemini'" type="success"
              >Gemini</el-tag
            >
            <span v-else>{{ row.modelType }}</span>
          </template>
        </el-table-column>
        <el-table-column
          prop="modelName"
          label="模型名称"
          min-width="120"
        ></el-table-column>
        <el-table-column
          prop="apiUrl"
          label="API 地址"
          min-width="200"
          show-overflow-tooltip
        ></el-table-column>
        <el-table-column
          prop="maxTokens"
          label="最大 Tokens"
          min-width="100"
        ></el-table-column>
        <el-table-column
          prop="temperature"
          label="温度"
          min-width="80"
        ></el-table-column>
        <el-table-column
          prop="delaySeconds"
          label="延迟秒数"
          min-width="80"
        ></el-table-column>
        <el-table-column prop="isActive" label="当前启用" min-width="100">
          <template slot-scope="{ row }">
            <el-switch
              v-model="row.isActive"
              active-color="#13ce66"
              inactive-color="#ff4949"
              @change="handleEnableChange(row)"
              v-permissions="[
                'system:ai-config:enable',
                'system:ai-config:disable',
              ]"
            >
            </el-switch>
          </template>
        </el-table-column>
        <el-table-column
          prop="enableSmartReply"
          label="智能回复"
          min-width="100"
        >
          <template slot-scope="{ row }">
            <el-switch
              v-model="row.enableSmartReply"
              :active-value="1"
              :inactive-value="0"
              @change="handleSmartReplyChange(row)"
              v-permissions="['system:ai-config:toggleSmartReply']"
            >
            </el-switch>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="150" fixed="right">
          <template slot-scope="{ row }">
            <el-button
              type="text"
              icon="el-icon-edit"
              @click="$refs.operaWindow.open('编辑配置', row)"
              v-permissions="['system:ai-config:update']"
              >编辑</el-button
            >
            <el-button
              type="text"
              icon="el-icon-delete"
              @click="deleteById(row)"
              v-permissions="['system:ai-config:delete']"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <pagination
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
        :pagination="tableData.pagination"
      ></pagination>
    </template>

    <!-- 编辑窗口 -->
    <OperaAiConfigWindow
      ref="operaWindow"
      @success="handlePageChange(tableData.pagination.pageIndex)"
    />
  </TableLayout>
</template>

<script>
import TableLayout from '@/layouts/TableLayout'
import BaseTable from '@/components/base/BaseTable'
import Pagination from '@/components/common/Pagination'
import OperaAiConfigWindow from '@/components/system/ai/OperaAiConfigWindow'
import {
  enableModel,
  disableModel,
  toggleSmartReply
} from '@/api/system/aiConfig'

export default {
  name: 'AiModelList',
  extends: BaseTable,
  components: { TableLayout, Pagination, OperaAiConfigWindow },
  data () {
    return {
      searchForm: {
        modelType: '',
        isActive: ''
      }
    }
  },
  created () {
    this.config({
      module: 'AI 配置',
      api: '/system/aiConfig',
      'field.main': 'modelName'
    })
    this.search()
  },
  methods: {
    // 切换启用状态
    async handleEnableChange (row) {
      const action = row.isActive ? enableModel : disableModel
      const actionName = row.isActive ? '启用' : '禁用'
      try {
        await action(row.modelType)
        this.$tip.success(`${actionName}成功`)
        this.search() // 刷新列表，因为启用一个可能禁用其他
      } catch (e) {
        row.isActive = !row.isActive // 恢复状态
        this.$tip.apiFailed(e)
      }
    },
    // 切换智能回复
    async handleSmartReplyChange (row) {
      try {
        await toggleSmartReply(row.modelType)
        this.$tip.success('切换成功')
      } catch (e) {
        row.enableSmartReply = row.enableSmartReply === 1 ? 0 : 1
        this.$tip.apiFailed(e)
      }
    }
  }
}
</script>

<style scoped lang="scss"></style>
